import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { brandInfo } from '@/mocks/site'
import { faqIds } from '@/mocks/faq'

interface FaqSectionProps {
  ids?: string[]
  namespace?: string
}

export default function FaqSection({ ids = faqIds, namespace = 'faq' }: FaqSectionProps) {
  const { t } = useTranslation()
  const [openId, setOpenId] = useState<string | null>(ids[0] ?? null)

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id))
  }

  return (
    <section id="faq" className="w-full bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary-500" />
              <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
                {t(`${namespace}.label`)}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
              {t(`${namespace}.title`)}
            </h2>
            <p className="mt-6 text-base text-foreground-600 leading-relaxed">{t(`${namespace}.intro`)}</p>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="w-11 h-11 rounded-full flex items-center justify-center bg-primary-100 text-primary-700 mb-4">
                <i className="ri-chat-3-line text-xl" />
              </span>
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-2">
                {t(`${namespace}.cardTitle`)}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-5">{t(`${namespace}.cardText`)}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={brandInfo.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-whatsapp-line text-base" />
                  {t('common.whatsappUs')}
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:border-primary-400 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-mail-send-line text-base" />
                  {t('common.sendEnquiry')}
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              {ids.map((id, index) => {
                const isOpen = openId === id
                return (
                  <div key={id} className={index !== 0 ? 'border-t border-background-200' : ''}>
                    <button
                      type="button"
                      onClick={() => toggle(id)}
                      aria-expanded={isOpen}
                      className="w-full flex items-center justify-between gap-4 text-left px-6 md:px-8 py-5 md:py-6 cursor-pointer hover:bg-background-100/60 transition-colors"
                    >
                      <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                        {t(`${namespace}.items.${id}.q`)}
                      </span>
                      <span
                        className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center border transition-all duration-300 ${
                          isOpen
                            ? 'bg-primary-500 border-primary-500 text-background-50 rotate-45'
                            : 'border-background-300 text-foreground-600'
                        }`}
                      >
                        <i className="ri-add-line text-lg" />
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-6 md:px-8 pb-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                          {t(`${namespace}.items.${id}.a`)}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}